'use client'

import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import CollapseItem from './collapse-item'
import GuestInfo from './guest-info'
import Message from '../ui/message'
import { useBookingCart } from '~/stores/booking-cart'
import { formatSingleDateShort } from '~/lib/date-utils'
import { generateId } from '~/lib/common-utils'
import { Spinner } from '../ui/spinner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import OverlayLoading from '../ui/overlay-loading'
import { useBooking } from '~/hooks/use-payment'
import type { ApiResponse, BookingResponse, BookingRequest, VoucherInfo } from '~/hooks/type'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { usePaymentData } from '~/hooks/use-payment-data'
import { Image } from '~/components/ui/image'
import VoucherInfoModal from '~/components/payment/voucher-modal'
import { useVoucherInfo } from '~/hooks/use-voucher-info'
import { useMediaQuery } from '~/hooks/use-media-query'
import { useMemberInfo } from '~/hooks/use-member-info'
import { useLoyaltySession } from '~/hooks/use-loyalty-session'
import { Ticket } from 'lucide-react'

interface Step1Props {
  nextStep: () => void
  hotelId: string
  arrivalDate: string
  departureDate: string
  timeIn: string
  timeOut: string
  nights: number
  totalRooms: number
  totalAmount: number
  totalChild?: number
  totalAdult?: number
  extra_guest_desc?: number
}

// 🎯 Zod schema factory — accepts t() for i18n validation messages
function createGuestSchema(t: (key: string) => string) {
  return z.object({
    fullName: z.string().min(1, t('guestInfo.fullNameRequired')),
    gender: z.string().min(1, t('guestInfo.genderRequired')),
    phone: z
      .string()
      .min(1, t('guestInfo.phoneRequired'))
      .regex(/^(0|\+84)\d{9}$/, t('guestInfo.phoneInvalid')),
    email: z
      .string()
      .min(1, t('guestInfo.emailRequired'))
      .refine(
        (val) => /^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)*@[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)+$/.test(val),
        t('guestInfo.emailInvalid')
      ),
  })
}

type GuestForm = {
  fullName: string
  gender: string
  phone: string
  email: string
}

export default function Step1({
  nextStep,
  hotelId,
  arrivalDate,
  departureDate,
  timeIn,
  timeOut,
  nights,
  totalAmount,
}: Step1Props) {
  const { items } = useBookingCart()
  const { createBooking, isLoading } = useBooking()
  const [error, setError] = useState<string | null>(null)
  const [isAgreed, setIsAgreed] = useState(false)
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { paymentData, setPaymentData, clearPaymentData } = usePaymentData()
  const { verifyOrder } = useVoucherInfo()

  const [showInput, setShowInput] = useState(true)
  const [inputValue, setInputValue] = useState<string>('')
  const [isApplied, setIsApplied] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [voucherInfo, setVoucherInfo] = useState<VoucherInfo>()
  const isDesktop = useMediaQuery('(min-width: 768px)')

  // Auth + loyalty vouchers
  const { token: authToken } = useLoyaltySession()
  const isLoggedIn = !!authToken
  const prevAuthTokenRef = useRef<string | null>(authToken)
  const { data: memberInfo } = useMemberInfo()
  const memberVouchers = memberInfo?.vouchers ?? []
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [voucherSearch, setVoucherSearch] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const displayVouchers = useMemo(() => {
    if (!isLoggedIn || !isDropdownOpen) return []
    return memberVouchers
  }, [memberVouchers, isDropdownOpen, isLoggedIn])

  const filteredVouchers = useMemo(() => {
    const kw = voucherSearch.trim().toLowerCase()
    if (!kw) return displayVouchers
    return displayVouchers.filter((v) => (v.code ?? v.voucherCode ?? '').toLowerCase().includes(kw))
  }, [displayVouchers, voucherSearch])

  // Click outside dropdown
  useEffect(() => {
    if (!isDropdownOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDropdownOpen])

  const bookingSummary = useMemo(() => {
    const rate = totalAmount
    return {
      dateIn: arrivalDate,
      timeIn,
      dateOut: departureDate,
      timeOut,
      totalTime: nights,
      totalRoom: items.length,
      rate,
      formattedRate: new Intl.NumberFormat('vi-VN').format(Number(rate)) + ' đ',
      formattedDiscountRate: '',
      formattedTotalRate: new Intl.NumberFormat('vi-VN').format(Number(rate)) + ' đ',
    }
  }, [arrivalDate, departureDate, nights, items.length, totalAmount, timeIn, timeOut])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    trigger,
    getValues,
  } = useForm<GuestForm>({
    resolver: zodResolver(createGuestSchema(t)),
    // Validate only on submit — filling fields incompletely/incorrectly must
    // stay silent (no inline errors) while the user is still typing. RHF
    // switches to per-change revalidation for a field only after its first
    // submit-triggered validation, which is exactly "validate, then keep it
    // accurate" without validating pre-emptively on mount or on every change.
    mode: 'onSubmit',
    defaultValues: {
      fullName: '',
      gender: 'Mr',
      phone: '',
      email: '',
    },
  })

  useEffect(() => {
    const voucherCode =
      new URLSearchParams(window.location.search).get('voucherCode') ||
      window.sessionStorage.getItem('voucherCode') ||
      ''
    if (voucherCode && !paymentData?.voucherCode) {
      setInputValue(voucherCode)
      setVoucherSearch(voucherCode)
      handleClick()
    }
  }, [])

  const clearGuestFields = useCallback(() => {
    // Silent reset: no `shouldValidate`/`trigger()` here on purpose. This
    // runs on fresh mount / auth transitions to blank out fields the user
    // hasn't touched yet — validating right away would flash "required"
    // errors on a screen the user just landed on, before they've typed
    // anything. Real interaction (typing, blur, submit) still validates
    // normally per the form's mode: 'onSubmit'.
    setValue('fullName', '')
    setValue('gender', 'Mr')
    setValue('phone', '')
    setValue('email', '')
  }, [setValue])

  useEffect(() => {
    // Wait for the sessionStorage-backed paymentData query to resolve first —
    // otherwise "not loaded yet" would look like "nothing cached".
    if (paymentData === undefined) return

    // Whose identity/voucher does paymentData currently reflect (if any)?
    // - If a booking already exists this session, its persisted `authOwner`
    //   marker survives full page reloads — which is how a *real* login/
    //   logout actually happens here (redirect to the portal and back) —
    //   so it's the source of truth for detecting a stale owner.
    // - Otherwise (no booking yet, nothing persisted to compare against)
    //   fall back to an in-memory ref that starts out equal to the current
    //   token, so a fresh mount with nothing cached never falsely reports a
    //   mismatch, while a live change during this same mount (e.g. logout in
    //   another tab) is still caught.
    const cachedOwner = paymentData ? (paymentData.authOwner ?? null) : prevAuthTokenRef.current
    const ownerChanged = cachedOwner !== authToken
    prevAuthTokenRef.current = authToken

    if (ownerChanged) {
      // Whatever is on the form, cached in paymentData, or applied as a
      // voucher belongs to a different auth state (a different account, or
      // no account at all) than the one now active — never let it leak.
      clearGuestFields()
      setIsApplied(false)
      setShowInput(true)
      setInputValue('')
      setVoucherSearch('')
      setIsDropdownOpen(false)
      setVoucherInfo(undefined)
      bookingSummary.formattedDiscountRate = ''
      bookingSummary.formattedTotalRate =
        new Intl.NumberFormat('vi-VN').format(Number(bookingSummary.rate)) + ' đ'

      if (paymentData) {
        setPaymentData({
          ...paymentData,
          customerName: '',
          fullName: '',
          gender: '',
          phone: '',
          email: '',
          voucherCode: '',
          authOwner: authToken,
        })
      }
      // Wait for the effect to re-run — either once the paymentData
      // sanitization above resolves, or once the newly logged-in account's
      // memberInfo arrives — rather than filling anything back in now.
      return
    }

    if (paymentData?.customerName || paymentData?.phone || paymentData?.email) {
      // Guest info already saved this session — e.g. a booking was already
      // created and the user came back to Step 1 (Step 2 -> back). Reuse it
      // as-is regardless of login state: re-deriving from the account
      // profile here would silently overwrite whatever was actually typed/
      // edited (or already used to create the booking) with the raw
      // profile values, which is exactly what going back looked like —
      // fields not matching payment data anymore.
      setValue('fullName', paymentData.customerName || '')
      setValue('gender', paymentData.gender || 'Mr')
      setValue('phone', paymentData.phone || '')
      setValue('email', paymentData.email || '')
    } else if (isLoggedIn) {
      if (!memberInfo) return // still fetching the current account's profile — stay blank
      const profile = {
        ...memberInfo,
        ...memberInfo?.data,
        ...memberInfo?.member,
        ...memberInfo?.profile,
      }
      const fullName = profile.full_name || profile.name || ''
      const phone = profile.phone || profile.mobile || ''
      const email = profile.email || ''
      const sex = profile.sex || profile.gender || ''
      // No `shouldValidate`/`trigger()` here either — this is an automatic
      // autofill, not user input. If the account profile is missing a field
      // (e.g. no phone on file), the user shouldn't see a "required" error
      // before they've had a chance to fill it in themselves; validation
      // only ever runs on submit now (mode: 'onSubmit').
      setValue('gender', sex === 'F' ? 'Mrs' : 'Mr')
      setValue('fullName', fullName)
      setValue('phone', phone)
      setValue('email', email)
    }

    if (paymentData?.voucherCode && !isApplied) {
      setInputValue(paymentData.voucherCode)
      handleClick()
    }
  }, [authToken, isLoggedIn, memberInfo, paymentData, setValue, trigger, clearGuestFields])

  const splitFullName = useCallback((fullName: string) => {
    const trimmed = fullName.trim()
    const lastSpaceIndex = trimmed.lastIndexOf(' ')
    return {
      firstName: lastSpaceIndex !== -1 ? trimmed.slice(0, lastSpaceIndex) : trimmed,
      lastName: lastSpaceIndex !== -1 ? trimmed.slice(lastSpaceIndex + 1) : '',
    }
  }, [])

  const onSubmit = async (data: GuestForm) => {
    const { firstName, lastName } = splitFullName(data.fullName)

    const bookingData: BookingRequest = {
      bookingRefId: generateId(10),
      bookingSource: 'MHG',
      hotelId,
      arrivalDate,
      departureDate,
      bookingItems: items.map((item) => ({
        roomId: item.roomId,
        quantity: 1,
        adults: item.guests.adults,
        underAge6: item.guests.childrenUnder6,
        underAge12: item.guests.children6To12,
        children: item.guests.childrenUnder6 + item.guests.children6To12,
        extraAdults: 0,
        extraChildren: 0,
        discount: 0,
        // amount: totalAmount,
        deposit: 0,
      })),
      guestInfo: {
        title: data.gender,
        firstName,
        lastName,
        email: data.email || '',
        phone: data.phone,
        address: '',
        country: '',
      },
      extraServices: [],
      payments: [],
      additionalNotes: '',
      discount: 0,
      deposit: 0,
      taxFee: 0,
      currencyISO: 'đ',
      voucherCode: voucherInfo?.voucherCode || '',
    }

    try {
      createBooking(bookingData, {
        onSuccess: (response: ApiResponse<BookingResponse>) => {
          if (response.isSuccess && response.data?.bookingNo) {
            // Tính thời điểm hết hạn từ cancelTime
            const cancelTimeStamp = new Date().getTime() + response.data.cancelTime * 60 * 1000
            localStorage.setItem('PaymentDeadline', cancelTimeStamp.toString())

            // Lưu thông tin guest vào payment data
            const paymentData = {
              bookingNo: response.data.bookingNo,
              bookingId: response.data.bookingId,
              customerName: data.fullName,
              fullName: data.fullName,
              gender: data.gender,
              phone: data.phone,
              email: data.email,
              cardItems: items,
              arrivalDate,
              departureDate,
              totalAmount: response.data.totalAmount,
              discountAmount: response.data.discount,
              subAmount: response.data.subAmount,
              timeIn: bookingSummary.timeIn,
              timeOut: bookingSummary.timeOut,
              nights: bookingSummary.totalTime,
              totalRooms: bookingSummary.totalRoom,
              cancelTime: cancelTimeStamp, // Lưu timestamp thay vì số phút
              voucherCode: voucherInfo?.voucherCode,
              // Marks which auth state this identity/voucher belongs to, so a
              // later mount (even after a full page reload, e.g. the real
              // logout redirect) can detect it's stale for a different account.
              authOwner: authToken,
            }
            setPaymentData(paymentData)
            setTimeout(() => {
              nextStep()
            })
          } else {
            setError(response.errors?.[0]?.message || t('payment.bookingFailed'))
          }
        },
        onError: (error: Error) => {
          setError(error.message || t('payment.bookingFailed'))
        },
      })
    } catch (error) {
      setError(t('payment.bookingFailed'))
    }
  }

  const handleAgree = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Just record the checkbox state — validation stays deferred to submit
    // ("Tiếp tục"), it must not fire just from checking "agree".
    setIsAgreed(e.target.checked)
  }, [])

  // Fires when "Tiếp tục" is pressed and validation (now first run at that
  // moment, per mode: 'onSubmit') fails — scroll up so the user can see the
  // errors RHF just populated.
  const onInvalid = useCallback(() => {
    scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (voucherInfo) {
      const totalDiscount =
        voucherInfo.valueType === 1
          ? voucherInfo.amountRemain
          : (bookingSummary.rate * voucherInfo.typePT) / 100
      const discountRate =
        voucherInfo.amountRemain !== undefined && voucherInfo.amountRemain !== null
          ? Math.min(totalDiscount, bookingSummary.rate, voucherInfo.amountRemain)
          : Math.min(totalDiscount, bookingSummary.rate)
      bookingSummary.formattedDiscountRate =
        new Intl.NumberFormat('vi-VN').format(discountRate) + ' đ'
      const total = Number(bookingSummary.rate) - discountRate
      bookingSummary.formattedTotalRate = new Intl.NumberFormat('vi-VN').format(total) + ' đ'
    }
  }, [voucherInfo])

  const handleClick = async () => {
    if (!isApplied) {
      setIsDropdownOpen(false)
      const params = {
        voucherCode: inputValue || paymentData?.voucherCode || '',
        hotelId: hotelId,
        // paymentData.phone is only set after a booking is created (onSuccess of
        // createBooking) — at the point the user applies a voucher on Step 1 that's
        // still empty, so read the phone that's actually on the form right now
        // (filled from memberInfo when logged in, or typed manually otherwise).
        phone: getValues('phone') || paymentData?.phone || '',
      }
      await verifyOrder(params).then((res) => {
        if (res && res.isSuccess) {
          if (
            res.data.voucherType === 'O' ||
            (res.data.voucherType === 'M' && res.data.valueType === 2 && res.data.numberUse === 1)
          ) {
            setVoucherInfo(res.data)
            setIsApplied(true)
            setShowInput(!showInput)
            const totalDiscount =
              res.data.valueType === 1
                ? res.data.amountRemain
                : (bookingSummary.rate * res.data.typePT) / 100
            const discountRate =
              res.data.amountRemain !== undefined && res.data.amountRemain !== null
                ? Math.min(totalDiscount, bookingSummary.rate, res.data.amountRemain)
                : Math.min(totalDiscount, bookingSummary.rate)
            bookingSummary.formattedDiscountRate =
              new Intl.NumberFormat('vi-VN').format(discountRate) + ' đ'
            const total = Number(bookingSummary.rate) - discountRate
            bookingSummary.formattedTotalRate = new Intl.NumberFormat('vi-VN').format(total) + ' đ'
          } else {
            setError(t('voucher_detail.error.type_invalid'))
          }
        } else {
          setError(res.errors?.[0]?.message || t('payment.bookingFailed'))
        }
      })
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <OverlayLoading show={isLoading} />
        <div className="grid grid-cols-1 gap-1 md:grid-cols-6 md:gap-4">
          <div className="md:col-span-4">
            <CollapseItem title={t('booking.customerInfo')} active>
              <GuestInfo
                register={register}
                errors={errors}
                disablePhone={isLoggedIn && !!memberInfo?.member?.phone}
              />
              <label className="hidden cursor-pointer items-center gap-2 sm:flex">
                <input
                  type="checkbox"
                  checked={isAgreed}
                  onChange={(e) => setIsAgreed(e.target.checked)}
                  className="h-4 w-4 checked:bg-[#7A4E2C] hover:cursor-pointer checked:hover:bg-[#7A4E2C] focus:outline-none focus:ring-0 checked:focus:bg-[#7A4E2C]"
                />
                <span className="text-[12px] font-light text-[#2D2E32] md:text-[14px]">
                  {t('payment.agree')}
                </span>
              </label>
              <button
                type="submit"
                disabled={isLoading || !isAgreed}
                className={`hidden w-full rounded-[4px] px-4 py-3 font-lexend text-sm font-medium text-white transition-colors sm:block ${
                  isLoading || !isAgreed
                    ? 'cursor-not-allowed bg-[#c2b0a2]'
                    : 'bg-[#7A4E2C] hover:bg-[#7A4E2C] active:bg-[#694D2B]'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Spinner className="h-4 w-4" />
                    <span>{t('booking.processing')}</span>
                  </div>
                ) : (
                  t('booking.continue')
                )}
              </button>
            </CollapseItem>
          </div>

          <div className="bg-white md:col-span-2 md:rounded-[12px]">
            <CollapseItem title={t('booking.yourBooking')} active>
              <div className="rounded-[6px] bg-[#F3F3F3] p-3 text-[12px] text-[#2D2E32]">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-2">
                    <label className="text-[12px] font-light sm:text-[16px]">
                      {t('booking.checkIn')}
                    </label>
                    <span className="text-[12px] font-[400] sm:text-[16px]">
                      {formatSingleDateShort(bookingSummary.dateIn, locale, t)}
                    </span>
                    <span className="text-[12px] font-light sm:text-[16px]">
                      {t('booking.from')} {bookingSummary.timeIn}
                    </span>
                  </div>
                  <div className="h-[1px] w-[9%] bg-[#3A3B3F]" />
                  <div className="flex flex-col gap-2 text-right">
                    <label className="text-[12px] font-light sm:text-[16px]">
                      {t('booking.checkOut')}
                    </label>
                    <span className="text-[12px] font-[400] sm:text-[16px]">
                      {formatSingleDateShort(bookingSummary.dateOut, locale, t)}
                    </span>
                    <span className="text-[12px] font-light sm:text-[16px]">
                      {t('booking.before')} {bookingSummary.timeOut}
                    </span>
                  </div>
                </div>

                <div className="mt-2 flex justify-between border-t border-[#CED0D3] pt-3">
                  <div className="flex flex-1 flex-col items-start gap-2">
                    <label className="text-[12px] font-light sm:text-[16px]">
                      {t('booking.totalStay')}
                    </label>
                    <span className="text-[12px] font-[400] sm:text-[16px]">
                      {String(bookingSummary.totalTime).padStart(2, '0')} {t('booking.nights')}
                    </span>
                  </div>
                  <div className="h-[21px] w-[1px] bg-[#CED0D3]" />
                  <div className="flex flex-1 flex-col items-end gap-2">
                    <label className="text-[12px] font-light sm:text-[16px]">
                      {t('booking.totalRooms')}
                    </label>
                    <span className="text-[12px] font-[400] sm:text-[16px]">
                      {String(bookingSummary.totalRoom).padStart(2, '0')} {t('booking.rooms')}
                    </span>
                  </div>
                </div>
              </div>
            </CollapseItem>
            <div className="h-1 w-full bg-[#F3F3F3] sm:hidden" />
            <div className="mb-0 mt-4 w-full p-4 text-[12px] sm:pt-0">
              <h3 className="font-lora text-[16px] text-base font-medium text-[#2D2E32] sm:text-[20px]">
                {t('booking.voucher_code')}
              </h3>
              <div className="relative flex justify-between gap-4 pt-3 sm:pb-1">
                {showInput ? (
                  <div
                    className="h-44px min-w-0 flex-1 rounded-[4px]"
                    ref={isLoggedIn ? dropdownRef : undefined}
                  >
                    {isLoggedIn ? (
                      /* Đã đăng nhập — hiển thị dạng dropdown trigger */
                      <>
                        <div className="relative">
                          <input
                            ref={inputRef}
                            autoComplete="off"
                            value={inputValue}
                            placeholder={t('booking.voucher_placeholder')}
                            maxLength={250}
                            className="w-full rounded-[4px] border-[#CED0D3] bg-transparent pr-8"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setInputValue(e.target.value)
                              setVoucherSearch(e.target.value)
                            }}
                            onFocus={() => {
                              if (!isApplied) setIsDropdownOpen(true)
                            }}
                          />
                          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                            <svg
                              className={`h-4 w-4 text-[#666B74] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                        {/* Dropdown danh sách voucher member */}
                        {isDropdownOpen && !isApplied && (
                          <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-md border border-[#CED0D3] bg-white shadow-md">
                            <div className="custom-scroll max-h-60 overflow-auto rounded-md">
                              {filteredVouchers.length === 0 && (
                                <div className="px-4 py-3 text-sm text-[#666B74]">
                                  {t('booking.no_voucher')}
                                </div>
                              )}
                              {filteredVouchers.map((v, idx) => {
                                const code = v.code ?? v.voucherCode ?? ''
                                const desc = v.provider_payload?.Name ?? ''
                                const expiryDate = v.provider_payload?.DateEnd
                                  ? (() => {
                                      const d = new Date(v.provider_payload.DateEnd)
                                      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
                                    })()
                                  : ''
                                return (
                                  <div
                                    key={v.id ?? code ?? idx}
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      setInputValue(code)
                                      setVoucherSearch('')
                                      setIsDropdownOpen(false)
                                      inputRef.current?.blur()
                                    }}
                                    className={`flex cursor-pointer items-stretch gap-3 border-b border-[#F3F3F3] px-4 py-3 last:border-b-0 hover:bg-[#F8F4F0] ${inputValue === code ? 'bg-[#F8F4F0]' : 'bg-white'}`}
                                  >
                                    <div className="flex items-center">
                                      <Ticket className="h-8 w-8 text-[#7A4E2C]" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-base font-[500] text-[#2D2E32]">
                                        {code}
                                      </div>
                                      {desc && <div className="text-sm text-[#2D2E32]">{desc}</div>}
                                      {expiryDate && (
                                        <div className="mt-2 text-sm text-[#666B74]">
                                          HSD: {expiryDate}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Chưa đăng nhập — input text nhập tay */
                      <input
                        autoComplete="off"
                        value={inputValue}
                        placeholder={t('booking.voucher_placeholder')}
                        maxLength={250}
                        className="w-full rounded-[4px] border-[#CED0D3] bg-transparent"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setInputValue(e.target.value)
                        }}
                      />
                    )}
                  </div>
                ) : (
                  /* Đã áp dụng voucher — hiển thị chip */
                  <div className="h-44px min-w-0 flex-1 rounded-[4px] bg-[#F8F4F0] px-4 py-2">
                    <div className="flex h-[28px] w-fit items-center gap-2 rounded-[4px] bg-[#EBE3D6] px-4 py-1">
                      <span className="max-w-[220px] truncate">{inputValue}</span>
                      <Image
                        src={'/icons/cancel.svg'}
                        alt={t('common.remove')}
                        fill
                        aspectRatio={'1/1'}
                        objectFit={'contain'}
                        className="w-[10px] content-center"
                        onClick={() => {
                          setShowInput(true)
                          setInputValue('')
                          setVoucherSearch('')
                          setIsApplied(false)
                          setIsDropdownOpen(false)
                          setVoucherInfo(undefined)
                          bookingSummary.formattedDiscountRate = ''
                          bookingSummary.formattedTotalRate =
                            new Intl.NumberFormat('vi-VN').format(Number(bookingSummary.rate)) +
                            ' đ'
                        }}
                      />
                    </div>
                  </div>
                )}
                <button
                  disabled={isApplied || !inputValue}
                  onClick={handleClick}
                  type={'button'}
                  className={`w-[125px] rounded-[4px] px-4 py-3 text-sm font-medium text-white transition-colors sm:block ${isApplied || !inputValue ? 'cursor-not-allowed bg-[#c2b0a2]' : 'bg-[#7A4E2C] hover:bg-[#7A4E2C] active:bg-[#694D2B]'}`}
                >
                  {t('booking.apply')}
                </button>
              </div>
              {voucherInfo && (
                <>
                  <a
                    onClick={() => {
                      setIsOpen(true)
                    }}
                    className={'text-[#047819] underline'}
                  >
                    {t('booking.view_detail_voucher')}
                  </a>
                  <VoucherInfoModal
                    data={voucherInfo as any}
                    isOpen={isOpen}
                    onClose={() => {
                      setIsOpen(false)
                    }}
                    locale={locale}
                  ></VoucherInfoModal>
                </>
              )}
            </div>
            <div className="mb-20 mt-4 w-full p-4 text-[12px] sm:pt-0 md:mb-0">
              <h3 className="font-lora text-[16px] text-base font-medium text-[#2D2E32] sm:text-[20px]">
                {t('booking.paymentInfo')}
              </h3>
              <div className="flex justify-between pt-3 sm:pb-1">
                <label className="md:text-14px text-[12px] font-light text-[#2D2E32] sm:text-[14px]">
                  {t('booking.roomPrice')} (x{bookingSummary.totalRoom})
                </label>
                <span className="text-[14px] font-medium">{bookingSummary.formattedRate}</span>
              </div>
              <div className="flex justify-between pb-3 pt-1 sm:pb-1">
                <label className="md:text-14px text-[12px] font-light text-[#2D2E32] sm:text-[14px]">
                  {t('booking.discount_label')}
                </label>
                <span className="text-[14px] font-medium">
                  {bookingSummary.formattedDiscountRate
                    ? `-${bookingSummary.formattedDiscountRate}`
                    : 0}
                </span>
              </div>
              <hr />
              <div className="flex justify-between pt-3 sm:pb-1">
                <label className="md:text-14px text-[12px] font-medium text-[#2D2E32] sm:text-[14px] md:leading-[36px]">
                  {t('booking.roomPrice')}{' '}
                  <span className={'font-[350]'}>{t('booking.include_tax')}</span>
                </label>
                <span className="text-[14px] font-medium md:text-[24px] md:text-primary-500">
                  {bookingSummary.formattedTotalRate}
                </span>
              </div>
              <div className={`text-[10px] font-light leading-4 text-red-950 md:text-[12px]`}>
                {t('payment.note')}
              </div>

              <label className="mt-4 flex cursor-pointer items-center gap-2 sm:hidden">
                <input
                  type="checkbox"
                  checked={isAgreed}
                  onChange={handleAgree}
                  className="h-4 w-4 checked:bg-[#7A4E2C] hover:cursor-pointer checked:hover:bg-[#7A4E2C] focus:outline-none focus:ring-0 checked:focus:bg-[#7A4E2C]"
                />
                <span className="text-[12px] font-light text-[#2D2E32] md:text-[14px]">
                  {t('payment.agree')}
                </span>
              </label>
            </div>
            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white p-4 sm:hidden">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-lexend text-[12px] text-[#666B74]">
                    {t('booking.total')} ({t('booking.taxIncluded')})
                  </span>
                  <span className="font-lexend text-lg font-medium text-secondary-950">
                    {bookingSummary.formattedRate}
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !isAgreed}
                  className={`rounded-[4px] px-4 py-3 font-lexend text-sm font-medium text-white transition-colors ${
                    isLoading || !isAgreed
                      ? 'cursor-not-allowed bg-[#c2b0a2]'
                      : 'bg-[#7A4E2C] hover:bg-[#7A4E2C] active:bg-[#694D2B]'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <Spinner className="h-4 w-4" />
                      <span>{t('booking.processing')}</span>
                    </div>
                  ) : (
                    t('booking.continue')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
      {error && <Message message={error} onClose={() => setError(null)} />}
    </>
  )
}
